import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import TrustSignals from '../../components/ui/TrustSignals';
import TransactionTable from './components/TransactionTable';
import SearchAndFilter from './components/SearchAndFilter';
import SummaryStats from './components/SummaryStats';
import BulkActions from './components/BulkActions';
import ExportOptions from './components/ExportOptions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TransactionHistory = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  // Mock transaction data
  const mockTransactions = [
    {
      id: '1',
      originalUrl: 'https://www.polygon.technology',
      title: 'Polygon - Web3\'s Internet',
      timestamp: '2025-01-07T09:02:27.704Z',
      status: 'confirmed',
      transactionHash: '0x742d35cc6634c0532925a3b8d4c9db96590b5b8c1234567890abcdef',
      ipfsHash: 'QmYjtig7VJQ6XsnUjqqJvj7QaMcCAwtrgNdahSiFofrE7o',
      blockNumber: 45892341,
      gasUsed: 142500,
      gasPrice: '25.5',
      screenshotUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=300&h=200&fit=crop',
      fileSize: '2.4 MB',
      verificationStatus: 'verified'
    },
    {
      id: '2',
      originalUrl: 'https://ethereum.org/en/developers/',
      title: 'Ethereum Developer Documentation',
      timestamp: '2025-01-06T14:30:15.123Z',
      status: 'confirmed',
      transactionHash: '0x1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t1u2v3w4x5y6z',
      ipfsHash: 'QmAbC123dEf456GhI789JkL012MnO345PqR678StU901VwX234YzA567',
      blockNumber: 45889876,
      gasUsed: 138750,
      gasPrice: '28.2',
      screenshotUrl: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=300&h=200&fit=crop',
      fileSize: '3.1 MB',
      verificationStatus: 'verified'
    },
    {
      id: '3',
      originalUrl: 'https://docs.openzeppelin.com/contracts/',
      title: 'OpenZeppelin Smart Contract Documentation',
      timestamp: '2025-01-05T11:45:30.987Z',
      status: 'pending',
      transactionHash: '0x9z8y7x6w5v4u3t2s1r0q9p8o7n6m5l4k3j2i1h0g9f8e7d6c5b4a3',
      ipfsHash: 'QmXyZ987WvU654TsR321QpO098NmL765KjI432HgF109EdC876BaZ543',
      blockNumber: null,
      gasUsed: null,
      gasPrice: '30.0',
      screenshotUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop',
      fileSize: '1.8 MB',
      verificationStatus: 'pending'
    },
    {
      id: '4',
      originalUrl: 'https://ipfs.io/ipfs/QmYwAPJzv5CZsnA625s3Xf2nemtYgPpHdWEz79ojWnPbdG/readme',
      title: 'IPFS Documentation',
      timestamp: '2025-01-04T16:20:45.456Z',
      status: 'failed',
      transactionHash: '0xfailed123transaction456hash789example012345678901234567',
      ipfsHash: null,
      blockNumber: null,
      gasUsed: null,
      gasPrice: '22.1',
      screenshotUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
      fileSize: null,
      verificationStatus: 'failed'
    },
    {
      id: '5',
      originalUrl: 'https://metamask.io/developer/',
      title: 'MetaMask Developer Platform',
      timestamp: '2025-01-03T08:15:12.789Z',
      status: 'confirmed',
      transactionHash: '0xmeta456mask789developer012platform345678901234567890123',
      ipfsHash: 'QmDev123Meta456Mask789Platform012Developer345678901234567',
      blockNumber: 45885432,
      gasUsed: 145230,
      gasPrice: '26.8',
      screenshotUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=200&fit=crop',
      fileSize: '2.9 MB',
      verificationStatus: 'verified'
    }
  ];

  useEffect(() => {
    loadTransactionHistory();
  }, []);

  useEffect(() => {
    filterAndSortTransactions();
  }, [transactions, searchTerm, statusFilter, dateRange, sortBy, sortOrder]);

  const loadTransactionHistory = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setTransactions(mockTransactions);
    } catch (error) {
      console.error('Failed to load transaction history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTransactions = () => {
    let filtered = [...transactions];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(tx => 
        tx?.originalUrl?.toLowerCase()?.includes(searchLower) ||
        tx?.title?.toLowerCase()?.includes(searchLower) ||
        tx?.transactionHash?.toLowerCase()?.includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered?.filter(tx => tx?.status === statusFilter);
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate;
      
      switch (dateRange) {
        case '24h':
          startDate = new Date(now?.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        filtered = filtered?.filter(tx => new Date(tx?.timestamp) >= startDate);
      }
    }

    // Sort
    filtered?.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'timestamp':
          aVal = new Date(a?.timestamp);
          bVal = new Date(b?.timestamp);
          break;
        case 'status':
          aVal = a?.status;
          bVal = b?.status;
          break;
        case 'gasUsed':
          aVal = a?.gasUsed || 0;
          bVal = b?.gasUsed || 0;
          break;
        default:
          aVal = a?.[sortBy];
          bVal = b?.[sortBy];
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredTransactions(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleTransactionSelect = (transactionId, checked) => {
    if (checked) {
      setSelectedTransactions(prev => [...prev, transactionId]);
    } else {
      setSelectedTransactions(prev => prev?.filter(id => id !== transactionId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const currentPageTransactions = getCurrentPageTransactions()?.map(tx => tx?.id);
      setSelectedTransactions(currentPageTransactions);
    } else {
      setSelectedTransactions([]);
    }
  };

  const getCurrentPageTransactions = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTransactions?.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredTransactions?.length / itemsPerPage);
  };

  const handleViewDetails = (transaction) => {
    navigate(`/archive-details?tx=${transaction?.transactionHash}`);
  };

  const handleRetryTransaction = (transaction) => {
    // In a real app, this would retry the failed transaction
    console.log('Retrying transaction:', transaction?.id);
  };

  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on transactions:`, selectedTransactions);
    // Implement bulk actions here
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
              {/* Loading skeleton */}
              <div className="space-y-6">
                <div className="h-8 bg-muted rounded w-1/3 animate-pulse" />
                <div className="h-20 bg-muted rounded animate-pulse" />
                <div className="h-64 bg-muted rounded animate-pulse" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-3">
                <Icon name="History" size={28} className="text-primary" />
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Transaction History</h1>
                  <p className="text-muted-foreground">
                    View and manage your notarization transaction history
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <ExportOptions 
                  selectedTransactions={selectedTransactions}
                  allTransactions={filteredTransactions}
                />
                <Button
                  iconName="Plus"
                  onClick={() => navigate('/webpage-notarization')}
                >
                  New Archive
                </Button>
              </div>
            </div>

            {/* Summary Stats */}
            <SummaryStats transactions={transactions} />

            {/* Search and Filter */}
            <SearchAndFilter
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              dateRange={dateRange}
              setDateRange={setDateRange}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />

            {/* Bulk Actions */}
            {selectedTransactions?.length > 0 && (
              <BulkActions 
                selectedCount={selectedTransactions?.length}
                onBulkAction={handleBulkAction}
              />
            )}

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              {/* Transaction Table */}
              <div className="xl:col-span-3">
                <TransactionTable
                  transactions={getCurrentPageTransactions()}
                  selectedTransactions={selectedTransactions}
                  onTransactionSelect={handleTransactionSelect}
                  onSelectAll={handleSelectAll}
                  onViewDetails={handleViewDetails}
                  onRetryTransaction={handleRetryTransaction}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalPages={getTotalPages()}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  totalItems={filteredTransactions?.length}
                />
              </div>

              {/* Right Sidebar */}
              <div className="xl:col-span-1">
                <TrustSignals />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TransactionHistory;